import { useIntl } from 'react-intl';
import type { GridColDef, GridValidRowModel } from '../lib/dataGrid';
import { isoWeekYear } from '../features/timeTracking/timeMath';
import { toDayString } from '../utils/formatDate';

/**
 * The KW column: the ISO calendar week of a record's Aufnahmedatum, COMPUTED —
 * there is no stored field behind it, so it costs the schema nothing and can
 * never drift out of sync with the date it belongs to.
 *
 * Offered by every grid whose entity carries a `recordedAt`. The Zeiterfassung
 * is the one grid that does not use this: a time entry STORES its week (and its
 * ISO week-year with it, because the two disagree around New Year).
 *
 * A `number` column, so the filter panel offers = / > / < with a number input
 * ("KW >= 32") and sorting is numeric. The week is derived from the LOCAL day of
 * the stored UTC timestamp, the same conversion `formatDate` makes. No date
 * leaves the cell empty rather than showing a 0.
 *
 * ⚠ The week number alone does not order across years — KW 3/2026 and KW 3/2025
 * are the same value here. Sort by the Aufnahmedatum for a chronological view.
 */
export function useIsoWeekColumn<Row extends GridValidRowModel>(
    dateOf: (row: Row) => string | null | undefined
): GridColDef<Row> {
    const intl = useIntl();
    return {
        field: 'isoWeek',
        headerName: intl.formatMessage({ id: 'common.isoWeek' }),
        width: 70,
        type: 'number',
        // `isoWeekYear` takes a plain 'YYYY-MM-DD' day; recordedAt is a full ISO
        // timestamp, so it goes through the same normalisation the date cells use.
        valueGetter: (_value, row) => {
            const { week } = isoWeekYear(toDayString(dateOf(row)));
            return week || null;
        }
    };
}
